import React from 'react';
import { X } from 'lucide-react';
import { Event } from '../types';
import MapView from './MapView';
import { motion, AnimatePresence } from 'framer-motion';

interface MapModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  userLocation?: { lat: number; lng: number };
  currentUserId?: string;
  acceptedEventIds?: string[];
}

const MapModal: React.FC<MapModalProps> = ({
  isOpen,
  onClose,
  event,
  userLocation,
  currentUserId,
  acceptedEventIds = [],
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-4 md:inset-8 lg:inset-16 bg-theme-bg-primary rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-theme-border-primary bg-theme-bg-secondary">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-theme-text-main line-clamp-1">
                  {event.title}
                </h2>
                <p className="text-sm text-theme-text-secondary mt-0.5">
                  {event.category}
                </p>
              </div>
              <button
                onClick={onClose}
                className="ml-4 p-2 hover:bg-theme-bg-hover rounded-lg transition-colors"
                aria-label="Close map"
              >
                <X className="w-5 h-5 text-theme-text-secondary" />
              </button>
            </div>

            {/* Map */}
            <div className="flex-1 relative">
              <MapView
                events={[event]}
                selectedEvent={event}
                userLocation={userLocation}
                currentUserId={currentUserId}
                interactive={true}
                showUserLocation={true}
                acceptedEventIds={acceptedEventIds}
                className="w-full h-full"
              />
            </div>

            {/* Privacy Notice */}
            <div className="p-4 bg-theme-bg-secondary border-t border-theme-border-primary">
              <p className="text-sm text-theme-text-secondary text-center">
                {event.venueType === 'home' ? (
                  <>
                    <span className="font-medium">Privacy Protected:</span> Exact meeting point shared after acceptance
                  </>
                ) : (
                  <>General area shown for privacy</>
                )}
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MapModal;
